<!-- src/components/TopBar.vue -->
<template>
  <div class="top-bar bg-gray-800 text-white fixed top-0 left-0 w-full z-50">
    <div class="container mx-auto px-4">
      <div class="flex items-center justify-between h-16">
        <!-- Logo et nom -->
        <div class="flex items-center">
          <router-link to="/" class="flex items-center">
            <img 
              src="/logo_anamnesis.svg" 
              alt="Logo Anamnesis" 
              class="h-8 w-8 text-white"
            />
            <span class="ml-2 font-bold text-xl">Anamnesis</span>
          </router-link>
        </div>

        <!-- Navigation desktop -->
        <div class="hidden md:flex items-center space-x-4">
          <!-- Sélecteur de langue - Toujours visible -->
          <div ref="langMenuContainer" class="relative">
            <button ref="langMenuButton" class="flex items-center px-1 py-1 rounded hover:bg-gray-700 text-sm" @click="toggleLangMenu">
              <span class="text-lg mr-1">{{ currentLocale === 'fr' ? '🇫🇷' : '🇬🇧' }}</span>
              <span class="uppercase">{{ currentLocale }}</span>
              <ChevronDownIcon class="h-4 w-4 ml-1" />
            </button>
            <div 
              v-show="isLangMenuOpen" 
              class="absolute left-0 mt-2 w-24 bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-600"
            >
              <button v-for="lang in availableLocales" :key="lang" class="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 flex items-center" :class="{ 'font-bold text-blue-400': currentLocale === lang }" @click="setLocale(lang)">
                <span class="text-lg mr-2">{{ lang === 'fr' ? '🇫🇷' : '🇬🇧' }}</span>
                <span class="uppercase">{{ lang }}</span>
              </button>
            </div>
          </div>
          
          <!-- Navigation utilisateur connecté -->
          <template v-if="isAuthenticated">
            <!-- Lien vers le dashboard -->
            <router-link to="/dashboard" class="flex items-center px-3 py-2 rounded hover:bg-gray-700">
              <LayoutDashboardIcon class="h-5 w-5 mr-1" />
              <span>{{$t('topbar.tools')}}</span>
            </router-link>
            
            <!-- Lien admin si utilisateur admin -->
            <router-link 
              v-if="isAdmin" 
              to="/admin" 
              class="flex items-center px-3 py-2 rounded hover:bg-gray-700"
            >
              <ShieldIcon class="h-5 w-5 mr-1" />
              <span>{{$t('topbar.admin')}}</span>
            </router-link>
            <!-- Menu utilisateur -->
            <div ref="userMenuContainer" class="relative">
              <button 
                class="flex items-center px-3 py-2 rounded hover:bg-gray-700" 
                @click="toggleUserMenu"
              >
                <UserIcon class="h-5 w-5" />
                <span class="ml-1 mr-1">{{ userName || $t('topbar.user') }}</span>
                <ChevronDownIcon class="h-4 w-4" />
              </button>
              
              <!-- Dropdown menu -->
              <div 
                v-show="isUserMenuOpen" 
                class="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-600"
              >
                <div class="px-4 py-2 text-xs text-gray-400 border-b border-gray-600">
                  {{ userEmail }}
                </div>
                <router-link 
                  to="/myprofile" 
                  class="block px-4 py-2 text-sm hover:bg-gray-700"
                  @click="closeUserMenu"
                >
                  <UserIcon class="h-4 w-4 inline mr-2" />
                  <span>{{$t('topbar.profile')}}</span>
                </router-link>
                <button 
                  class="w-full text-left block px-4 py-2 text-sm hover:bg-gray-700"
                  @click="handleLogout"
                >
                  <LogOutIcon class="h-4 w-4 inline mr-2" />
                  <span>{{$t('topbar.logout')}}</span>
                </button>
              </div>
            </div>
          </template>
          
          <!-- Boutons de connexion si non connecté -->
          <template v-else>
            <router-link 
              to="/auth?mode=login" 
              class="flex items-center px-4 py-2 bg-blue-600 rounded hover:bg-blue-700 transition-colors"
            >
              <LogInIcon class="h-4 w-4 mr-2" />
              <span>{{$t('topbar.login')}}</span>
            </router-link>
            <router-link 
              to="/auth?mode=register" 
              class="flex items-center px-4 py-2 border border-gray-400 rounded hover:bg-gray-700 transition-colors"
            >
              <UserPlusIcon class="h-4 w-4 mr-2" />
              <span>{{$t('topbar.register')}}</span>
            </router-link>
          </template>
        </div>

        <!-- Bouton menu burger (mobile) -->
        <div class="md:hidden">
          <button class="text-white focus:outline-none" @click="toggleMobileMenu">
            <MenuIcon v-if="!isMobileMenuOpen" class="h-6 w-6" />
            <XIcon v-else class="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>

    <!-- Menu mobile -->
    <div v-show="isMobileMenuOpen" class="md:hidden bg-gray-800 pb-4 px-4">
      <!-- Sélecteur de langue mobile - Toujours visible -->
      <div class="flex flex-col space-y-2 mb-4 border-b border-gray-600 pb-4">
        <div class="px-3 py-2 text-sm text-gray-300 font-medium">Langue / Language</div>
        <div class="flex space-x-2">
          <button 
            v-for="lang in availableLocales" 
            :key="lang" 
            class="flex items-center px-3 py-2 rounded text-sm flex-1 justify-center" 
            :class="currentLocale === lang ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'"
            @click="setLocale(lang)"
          >
            <span class="text-lg mr-2">{{ lang === 'fr' ? '🇫🇷' : '🇬🇧' }}</span>
            <span class="uppercase">{{ lang }}</span>
          </button>
        </div>
      </div>
      
      <!-- Liens de navigation mobile - Si connecté -->
      <div v-if="isAuthenticated" class="flex flex-col space-y-2">
        <!-- Info utilisateur -->
        <div class="px-3 py-2 text-sm text-gray-300 border-b border-gray-600 mb-2">
          <div class="font-medium">{{ userName }}</div>
          <div class="text-xs text-gray-400">{{ userEmail }}</div>
        </div>
        
        <router-link 
          to="/dashboard" 
          class="flex items-center px-3 py-2 rounded hover:bg-gray-700" 
          @click="closeMobileMenu"
        >
          <LayoutDashboardIcon class="h-5 w-5 mr-2" />
          <span>{{$t('topbar.tools')}}</span>
        </router-link>
                
        <router-link 
          v-if="isAdmin"
          to="/admin" 
          class="flex items-center px-3 py-2 rounded hover:bg-gray-700" 
          @click="closeMobileMenu"
        >
          <ShieldIcon class="h-5 w-5 mr-2" />
          <span>{{$t('topbar.admin')}}</span>
        </router-link>

        <router-link 
          to="/myprofile" 
          class="flex items-center px-3 py-2 rounded hover:bg-gray-700" 
          @click="closeMobileMenu"
        >
          <UserIcon class="h-5 w-5 mr-2" />
          <span>{{$t('topbar.profile')}}</span>
        </router-link>

        
        <button 
          class="flex items-center px-3 py-2 rounded hover:bg-gray-700 text-left w-full"
          @click="handleLogout"
        >
          <LogOutIcon class="h-5 w-5 mr-2" />
          <span>{{$t('topbar.logout')}}</span>
        </button>
      </div>
      
      <!-- Liens de connexion mobile - Si non connecté -->
      <div v-else class="flex flex-col space-y-2">
        <router-link 
          to="/auth?mode=login" 
          class="flex items-center px-3 py-2 bg-blue-600 rounded hover:bg-blue-700 transition-colors" 
          @click="closeMobileMenu"
        >
          <LogInIcon class="h-5 w-5 mr-2" />
          <span>{{$t('topbar.login')}}</span>
        </router-link>
        
        <router-link 
          to="/auth?mode=register" 
          class="flex items-center px-3 py-2 border border-gray-400 rounded hover:bg-gray-700 transition-colors" 
          @click="closeMobileMenu"
        >
          <UserPlusIcon class="h-5 w-5 mr-2" />
          <span>{{$t('topbar.register')}}</span>
        </router-link>
      </div>
    </div>

    <!-- Indicateur de chargement -->
    <div 
      v-if="isLoading" 
      class="absolute top-0 left-0 w-full h-1 bg-blue-600 animate-pulse"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores/auth";
import {
    ChevronDownIcon,
    LayoutDashboardIcon,
    LogInIcon,
    LogOutIcon,
    MenuIcon,
    ShieldIcon,
    UserIcon,
    UserPlusIcon,
    XIcon
} from "lucide-vue-next";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from 'vue-i18n';
import { useRouter } from "vue-router";

// Composables
const router = useRouter();
const authStore = useAuthStore();
const { locale } = useI18n();
const availableLocales = ['fr', 'en'];
const currentLocale = computed(() => locale.value);

// État local
const isMobileMenuOpen = ref(false);
const isUserMenuOpen = ref(false);
const userMenuContainer = ref<HTMLElement | null>(null);
const isLangMenuOpen = ref(false);
const langMenuContainer = ref<HTMLElement | null>(null);

// Computed depuis le store
const isAuthenticated = computed(() => authStore.isAuthenticated);
const isAdmin = computed(() => authStore.isAdmin);
const userName = computed(() => authStore.userName);
const userEmail = computed(() => authStore.userEmail);
const isLoading = computed(() => authStore.isLoading);

// Méthodes
const toggleMobileMenu = (): void => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  if (isMobileMenuOpen.value) {
    isUserMenuOpen.value = false;
  }
};

const closeMobileMenu = (): void => {
  isMobileMenuOpen.value = false;
};

const toggleUserMenu = (): void => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const closeUserMenu = (): void => {
  isUserMenuOpen.value = false;
};

const closeUserMenuOnClickOutside = (event: MouseEvent): void => {
  if (userMenuContainer.value && !userMenuContainer.value.contains(event.target as Node)) {
    isUserMenuOpen.value = false;
  }
};

const toggleLangMenu = (): void => {
  isLangMenuOpen.value = !isLangMenuOpen.value;
};
const setLocale = (lang: string): void => {
  locale.value = lang;
  isLangMenuOpen.value = false;
};

const closeLangMenuOnClickOutside = (event: MouseEvent): void => {
  if (langMenuContainer.value && !langMenuContainer.value.contains(event.target as Node)) {
    isLangMenuOpen.value = false;
  }
};

const handleLogout = async (): Promise<void> => {
  try {
    await authStore.logout();
    closeUserMenu();
    closeMobileMenu();
    
    // Rediriger vers la page d'accueil
    await router.push('/');
  } catch (error) {
    console.error('Erreur lors de la déconnexion:', error);
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', closeUserMenuOnClickOutside);
  document.addEventListener('click', closeLangMenuOnClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', closeUserMenuOnClickOutside);
  document.removeEventListener('click', closeLangMenuOnClickOutside);
});
</script>

<style scoped>
/* Ajout d'un espace en haut du contenu pour compenser la hauteur de la barre de navigation */
:deep(.app-container) {
  padding-top: 4rem;
}

/* Animation pour le dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>